import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Button,
  Typography,
  makeStyles,
  createStyles,
  CircularProgress,
} from '@material-ui/core'
import { Alert } from '@mui/material'
import { connect } from 'react-redux'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Cart } from '@/typings/base'
import {
  createSwiklyCaution,
  getSwiklyCautionStatus,
} from '@/redux/actions/swikly'
import { CreateRequestPayload, SwiklyLanguage } from '@/redux/services/swikly'
import Price from '@/components/Shop/Price'
import CMSText from '../Global/CMSText'
import clsx from 'clsx'
import { swiklyDepositPercentage } from '@/utils/constants'
import { RichTextField } from '@prismicio/types'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(3),
    },
    cautionInfo: {
      backgroundColor: theme.palette.grey[50],
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(2),
    },
    cautionAmount: {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    cautionButton: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(2),
    },
    statusContainer: {
      marginTop: theme.spacing(2),
    },
    successStatus: {
      color: theme.palette.success.main,
    },
    errorStatus: {
      color: theme.palette.error.main,
    },
  })
)

/**
 * Props for the SwiklyCaution component
 */
interface SwiklyCautionProps {
  /** Cart object containing items and billing information */
  cart: Cart
  /** CMS parameters for customization */
  parameters: any
  /** Function to create a new Swikly caution */
  createSwiklyCaution: (data: CreateRequestPayload) => Promise<any>
  /** Function to get the status of a Swikly caution */
  getSwiklyCautionStatus: (cautionId: string) => Promise<any>
  /** Callback function when caution is completed */
  onCautionComplete: (cautionId: string) => void
  /** Optional parameter for deposit expiration days (default: 30) */
  days?: number
  /** Optional parameter for deposit explanation */
  depositExplanation?: RichTextField
  /** Prismic funnel field for the Swikly caution explanatory text */
  cautionInfo?: RichTextField
}

/**
 * SwiklyCaution component for handling deposit payments
 *
 * This component manages the creation and status checking of Swikly deposits.
 * It handles the complete flow from creating a deposit to checking its status
 * after the user returns from the Swikly payment page.
 *
 * Features:
 * - Calculates deposit amount based on cart items and deposit percentage
 * - Creates Swikly deposit requests
 * - Redirects to Swikly payment page
 * - Checks deposit status on return from Swikly
 * - Handles various deposit statuses (pending, accepted, error)
 * - Supports multiple languages
 */
const SwiklyCaution: React.FC<SwiklyCautionProps> = ({
  cart,
  parameters,
  createSwiklyCaution,
  getSwiklyCautionStatus,
  onCautionComplete,
  days = 30,
  depositExplanation,
  cautionInfo,
}) => {
  const classes = useStyles()
  const { t, i18n } = useTranslation()
  const router = useRouter()

  const [cautionStatus, setCautionStatus] = useState<
    'pending' | 'accepted' | 'error' | null
  >(null)
  const [cautionId, setCautionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const isMountedRef = useRef(true)
  const processedUrlRef = useRef<string | null>(null)

  // Get deposit percentage from environment variable (as decimal)
  const depositPercentage = swiklyDepositPercentage
    ? parseFloat(swiklyDepositPercentage)
    : 0

  // Calculate deposit amount
  const depositAmount = useMemo(
    () =>
      cart.item?.items.reduce((acc, item) => {
        const originalPrice =
          item.product.variants[0]?.originalPrice?.current ?? 0
        if (originalPrice) {
          return acc + originalPrice * item.quantity * depositPercentage
        }
        return acc
      }, 0),
    [cart.item?.items]
  )

  // Check if caution is enabled - use environment variable instead of parameters
  const isCautionEnabled = depositPercentage > 0

  useEffect(() => {
    // Check if there's a return from Swikly with status parameters
    const { status, reference } = router.query
    const currentUrl = router.asPath

    // Only proceed if we have a successful status and haven't already set a caution ID
    // and we haven't already processed this URL
    if (
      status === 'OK' &&
      !cautionId &&
      !isCheckingStatus &&
      !loading &&
      processedUrlRef.current !== currentUrl
    ) {
      processedUrlRef.current = currentUrl

      if (reference) {
        // Use the reference parameter to check status
        setCautionId(reference as string)
        checkCautionStatus(reference as string)
      }
    }

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Reset loading state when component unmounts or router changes
  useEffect(() => {
    return () => {
      if (isMountedRef.current) {
        setLoading(false)
        setIsCheckingStatus(false)
      }
    }
  }, [])

  const checkCautionStatus = async (id: string) => {
    // Prevent multiple simultaneous calls
    if (isCheckingStatus || loading) {
      return
    }

    try {
      setIsCheckingStatus(true)
      setLoading(true)
      const response = await getSwiklyCautionStatus(id)

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) {
        return
      }

      const request = response.request
      const status = request.deposit?.status

      // Check if the customId matches the cart tokenValue
      if (request.customId !== cart.item.tokenValue) {
        setCautionStatus('error')
        setError(t('texts:funnel:payment:caution_token_mismatch'))
        // Clear router query to prevent reprocessing the same invalid caution
        router.replace(router.pathname, undefined, { shallow: true })
        return
      }

      // Check deposit status based on Swikly API spec
      if (['Accepted', 'Released'].includes(status)) {
        setCautionStatus('accepted')
        onCautionComplete(id)
      } else if (
        ['Expired', 'Canceled', 'Failed', 'ExpiredWithoutAcceptance'].includes(
          status
        )
      ) {
        setCautionStatus('error')
        setError(t('texts:funnel:payment:caution_expired'))
      } else if (['Pending', 'Initialized', 'Temporary'].includes(status)) {
        setCautionStatus('pending')
      } else {
        setCautionStatus('pending')
      }
      setLoading(false)
      setIsCheckingStatus(false)
    } catch (err) {
      // Check if component is still mounted before updating state
      if (isMountedRef.current) {
        setCautionStatus('error')
        setError(t('texts:funnel:payment:caution_error'))
        setLoading(false)
        setIsCheckingStatus(false)
      }
    }
  }

  const handleCreateCaution = async () => {
    if (!isCautionEnabled || depositAmount <= 0) return

    try {
      setLoading(true)
      setError(null)

      const cartItem = cart.item

      const customerEmail = localStorage.getItem('email')
      const {
        firstName,
        lastName,
        countryCode,
        city,
        street,
        postcode,
        phoneNumber,
      } = cartItem?.billingAddress || {}

      // Calculate end date (add days to the end leasing date)
      const endDate = dayjs(cartItem.endLeasingDate, 'DD/MM/YYYY').add(
        days,
        'day'
      )

      // Get language from i18n with validation - use lowercase for Swikly API
      const currentLang = (i18n.language?.toLowerCase() || 'en') as string

      const getSwiklyLanguage = (lang: string): SwiklyLanguage => {
        switch (lang) {
          case 'fr':
            return SwiklyLanguage.FR
          case 'en':
            return SwiklyLanguage.EN
          case 'nl':
            return SwiklyLanguage.NL
          case 'de':
            return SwiklyLanguage.DE
          case 'es':
            return SwiklyLanguage.ES
          case 'it':
            return SwiklyLanguage.IT
          default:
            return SwiklyLanguage.EN
        }
      }

      const swiklyLang = getSwiklyLanguage(currentLang)

      // As per the Swikly API spec table of parameters
      const cautionData: CreateRequestPayload = {
        description: t('texts:funnel:payment:swikly_deposit_description'),
        language: swiklyLang,
        customId: cart.item.tokenValue, // Custom ID
        customIdMustBeUnique: true,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: customerEmail,
        phoneNumber: phoneNumber,
        redirectUrl: window.location.href,
        callbacks: {
          // Note: Webhook endpoint removed - status updates handled via return URL
        },
        deposit: {
          startDate: dayjs().format('YYYY-MM-DD'), // Use date format, not ISO string
          endDate: endDate.format('YYYY-MM-DD'), // Use date format, not ISO string
          amount: depositAmount, // Amount in cents
        },
        address: {
          country: countryCode,
          city: city,
          street: street,
          postcode: postcode,
        },
      }

      const caution = await createSwiklyCaution(cautionData)
      setCautionId(caution.request.id)

      // Redirect to Swikly payment page
      if (caution.request.link) {
        window.location.href = caution.request.link
      }
    } catch (err) {
      setError(t('texts:funnel:payment:caution_creation_error'))
    } finally {
      setLoading(false)
    }
  }

  if (!isCautionEnabled || depositAmount <= 0) {
    return null
  }

  if (cautionStatus === 'accepted') {
    return (
      <div className={classes.root}>
        <Alert severity="success" className={classes.statusContainer}>
          <CMSText
            asText
            data={parameters?.caution_paid_message}
            defaultText={t('texts:funnel:payment:caution_paid')}
          />
        </Alert>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.cautionInfo}>
        <Typography variant="body2" gutterBottom>
          <CMSText
            asText
            data={cautionInfo}
            defaultText={t('texts:funnel:payment:caution_info')}
          />
        </Typography>
        <Typography variant="h6" className={classes.cautionAmount}>
          {t('texts:basket:deposit')}:{' '}
          <Price amount={depositAmount} showDayOrMonth={false} />
        </Typography>
        <Typography variant="caption" color="textSecondary">
          <CMSText
            asText
            data={depositExplanation}
            defaultText={t('texts:basket:deposit_explanation')}
          />
        </Typography>
      </div>

      {error && (
        <Alert severity="error" className={classes.statusContainer}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            {t('texts:funnel:payment:processing_caution')}
          </Typography>
        </div>
      ) : (
        <Button
          variant="outlined"
          color="primary"
          className={clsx(classes.cautionButton, 'swikly-caution-button')}
          onClick={handleCreateCaution}
          disabled={cautionStatus === 'error'}
        >
          <CMSText
            asText
            data={parameters?.caution_button}
            defaultText={t('texts:funnel:payment:pay_caution')}
          />
        </Button>
      )}
    </div>
  )
}

const mapDispatchToProps = {
  createSwiklyCaution,
  getSwiklyCautionStatus,
}

export default connect(null, mapDispatchToProps)(SwiklyCaution)
